import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Truck, Warehouse, Package, ArrowRight, Star, ChevronRight, LogIn, User, Loader2, Shield, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BG = "https://images.unsplash.com/photo-1766608422198-5be9ac0aac9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxmcmVpZ2h0JTIwdHJ1Y2slMjBoaWdod2F5fGVufDB8fHx8MTc3MzQzMzAwNHww&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

export default function HomePage() {
  const { t } = useTranslation();
  const [trackingNumber, setTrackingNumber] = useState("");
  const [reviews, setReviews] = useState([]);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    const token = localStorage.getItem("mv_token");
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => { setIsLoggedIn(true); setUserName(r.data.name); })
        .catch(() => { localStorage.removeItem("mv_token"); });
    }
  }, []);

  const handleClientLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) { toast.error("Inserisci email e password"); return; }
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: loginEmail, password: loginPassword });
      localStorage.setItem("mv_token", res.data.token);
      toast.success(`Benvenuto, ${res.data.name}!`);
      navigate("/tracking");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenziali non valide");
    } finally { setLoginLoading(false); }
  };

  const handleTracking = (e) => {
    e.preventDefault();
    if (trackingNumber.trim()) {
      navigate(`/tracking?q=${trackingNumber.trim()}`);
    }
  };

  return (
    <main>
      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-center bg-[#09090B] overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090B] via-[#09090B]/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-32 md:py-40">
          <motion.div {...fadeUp} className="max-w-3xl">
            <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-4 font-heading">
              {t("hero.tagline")}
            </p>
            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight uppercase leading-[0.9] mb-6">
              {t("hero.title_l1")}
              <br />
              <span className="text-[#FF5200]">{t("hero.title_l2")}</span>
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl max-w-xl mb-10 font-body">
              {t("hero.subtitle")}
            </p>

            <form
              onSubmit={handleTracking}
              className="flex gap-0 max-w-lg mb-10"
              data-testid="hero-tracking-form"
            >
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder={t("hero.tracking_placeholder")}
                className="flex-1 h-14 px-5 bg-white/10 border-2 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-[#FF5200] outline-none rounded-sm font-body"
                data-testid="hero-tracking-input"
              />
              <button
                type="submit"
                className="h-14 px-6 bg-[#FF5200] text-white font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200"
                data-testid="hero-tracking-submit"
              >
                <Search size={20} />
              </button>
            </form>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/preventivo"
                data-testid="hero-cta-preventivo"
                className="inline-flex items-center justify-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm"
              >
                {t("hero.cta_quote")} <ArrowRight size={18} />
              </Link>
              <Link
                to="/servizi"
                data-testid="hero-cta-servizi"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-white hover:text-[#09090B] transition-colors duration-200 font-heading text-sm"
              >
                {t("hero.cta_services")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== SERVICES OVERVIEW ===== */}
      <section className="py-20 md:py-28 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="mb-14">
            <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-2 font-heading">
              {t("services_overview.tagline")}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-[#09090B]">
              {t("services_overview.title")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: Truck,
                title: t("services_overview.transport_title"),
                desc: t("services_overview.transport_desc"),
              },
              {
                icon: Package,
                title: t("services_overview.logistics_title"),
                desc: t("services_overview.logistics_desc"),
              },
              {
                icon: Warehouse,
                title: t("services_overview.warehouse_title"),
                desc: t("services_overview.warehouse_desc"),
              },
            ].map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
              >
                <Link
                  to="/servizi"
                  data-testid={`service-card-${service.title.toLowerCase()}`}
                  className="block border-2 border-zinc-200 bg-white p-8 md:p-10 hover:border-[#09090B] transition-colors duration-300 group h-full"
                >
                  <service.icon
                    size={44}
                    className="text-[#FF5200] mb-6"
                    strokeWidth={1.5}
                  />
                  <h3 className="font-heading text-2xl md:text-3xl font-bold uppercase mb-3 text-[#09090B]">
                    {service.title}
                  </h3>
                  <p className="text-zinc-500 leading-relaxed mb-6 font-body">
                    {service.desc}
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#FF5200] font-bold text-sm uppercase tracking-wide font-heading group-hover:gap-3 transition-all duration-300">
                    {t("services_overview.discover")} <ChevronRight size={16} />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="py-20 md:py-28 bg-[#FAFAFA] border-y-2 border-zinc-200" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { number: "10+", label: "Anni di esperienza" },
              { number: "250+", label: "Spedizioni mensili" },
              { number: "25+", label: "Partner logistici" },
              { number: "98%", label: "Consegne puntuali" },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                className="text-center"
                data-testid={`stat-${i}`}
              >
                <p className="font-heading text-4xl md:text-6xl font-bold text-[#FF5200]">
                  {stat.number}
                </p>
                <p className="text-sm font-bold tracking-widest uppercase text-zinc-500 mt-2 font-heading">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== AREA CLIENTI ===== */}
      <section className="py-20 md:py-28 bg-white" data-testid="client-area-home-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-2 font-heading">
                Area Riservata
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-[#09090B] mb-6">
                Area Clienti
              </h2>
              <p className="text-zinc-500 text-lg leading-relaxed mb-8 font-body">
                Accedi alla tua area personale per monitorare le tue spedizioni in tempo reale,
                consultare lo storico e ricevere aggiornamenti sullo stato di ogni consegna.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Package, text: "Traccia tutte le tue spedizioni" },
                  { icon: Search, text: "Consulta lo storico consegne" },
                  { icon: Shield, text: "Accesso sicuro e riservato" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FF5200]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon size={20} className="text-[#FF5200]" />
                    </div>
                    <p className="text-zinc-700 font-body font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              {isLoggedIn ? (
                <div className="border-2 border-zinc-200 p-8 md:p-10" data-testid="client-area-logged-in">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-[#FF5200] flex items-center justify-center">
                      <User size={28} className="text-white" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm font-heading uppercase tracking-widest">Bentornato</p>
                      <p className="text-xl font-bold text-[#09090B]">{userName}</p>
                    </div>
                  </div>
                  <Link
                    to="/tracking"
                    data-testid="client-area-go-dashboard"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm"
                  >
                    Vai alla Tua Area <ArrowRight size={18} />
                  </Link>
                </div>
              ) : (
                <div className="border-2 border-zinc-200 p-8 md:p-10" data-testid="client-area-login-form">
                  <h3 className="font-heading text-2xl font-bold uppercase mb-6 text-[#09090B]">
                    Accedi
                  </h3>
                  <form onSubmit={handleClientLogin} className="space-y-4">
                    <div>
                      <label htmlFor="home-login-email" className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                        Email
                      </label>
                      <input
                        id="home-login-email"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="w-full h-12 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                        placeholder="la.tua@email.com"
                        data-testid="home-login-email-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="home-login-password" className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          id="home-login-password"
                          type={showLoginPassword ? "text" : "password"}
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          className="w-full h-12 pl-4 pr-12 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                          placeholder="La tua password"
                          data-testid="home-login-password-input"
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                          aria-label={showLoginPassword ? "Nascondi password" : "Mostra password"}
                          data-testid="home-login-toggle-password"
                          tabIndex={-1}
                        >
                          {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loginLoading}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm disabled:opacity-50"
                      data-testid="home-login-submit-btn"
                    >
                      {loginLoading ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />}
                      Accedi all'Area Clienti
                    </button>
                  </form>
                  <p className="text-zinc-400 text-sm mt-4 text-center font-body">
                    Le credenziali vengono fornite al momento dell'attivazione del servizio.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== REVIEWS ===== */}
      {reviews.length > 0 && (
        <section className="py-20 md:py-28 bg-white" data-testid="reviews-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="mb-14">
              <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-2 font-heading">
                Testimonianze
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-[#09090B]">
                Cosa Dicono Di Noi
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {reviews.slice(0, 3).map((review, i) => (
                <motion.div
                  key={review.id || i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.12, duration: 0.5 }}
                  className="border-2 border-zinc-200 p-8 md:p-10"
                  data-testid={`review-card-${i}`}
                >
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <Star key={j} size={16} className="text-[#FF5200] fill-[#FF5200]" />
                    ))}
                  </div>
                  <p className="text-zinc-600 leading-relaxed mb-6 font-body italic">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div>
                    <p className="font-bold text-[#09090B]">{review.author}</p>
                    {review.company && (
                      <p className="text-sm text-zinc-500">{review.company}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== CTA ===== */}
      <section className="py-20 md:py-28 bg-[#09090B]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-white tracking-tight uppercase mb-6">
              Pronto a Spedire?
            </h2>
            <p className="text-zinc-400 text-lg max-w-xl mx-auto mb-10 font-body">
              Contattaci oggi per un preventivo gratuito e personalizzato. Il
              nostro team è a tua disposizione.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/preventivo"
                data-testid="cta-preventivo"
                className="inline-flex items-center justify-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm"
              >
                Richiedi Preventivo <ArrowRight size={18} />
              </Link>
              <Link
                to="/contatti"
                data-testid="cta-contatti"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-white hover:text-[#09090B] transition-colors duration-200 font-heading text-sm"
              >
                Contattaci
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
